/**
 * TasksView.jsx - the Tasks page (T-06): every person's tasks in one table.
 *
 * Paul: "One place to walk through every person's tasks in a weekly review."
 * Columns: done, task, owner, due (red when overdue), type (Lead /
 * Opportunity / Check-in / Project), home record (opens it), contact (opens
 * them), and the home record's stage. Filters: person, type, due window and
 * Open / Completed, plus a "Group by person" switch. Each user's filters are
 * remembered in this browser.
 *
 * Ticking a task goes through the shared useCompleteTask, exactly like the
 * Dashboard and the record panels: the optional completion note first (T-09),
 * then "What's the next step?" if it was the last open task on a lead or an
 * open opportunity (T-04).
 *
 * Won-project tasks are PM work and stay a separate list (Paul Q10), so the
 * default Type leaves them out; picking Project or Every type shows them.
 *
 * Tagged people (10/6/26): the Owner column lists the people tagged under the
 * owner; Person (Me or a name) matches a task its person owns OR is tagged
 * on, and a task that is mine only because I was tagged says "Tagged".
 *
 * Main tabs only - never rendered inside the GLOBAL KSA scope, so it reads
 * the plain /api path. Row shape, filters and ordering live in
 * components/tasks/tasksTable.js.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { STAGE_COLORS } from '../data/crmStages';
import { fmtDate } from '../utils/format';
import { contactIndex, indexById } from '../components/crm/nextStep';
import { ContactLink } from '../components/tasks/TaskComposer';
import { TaggedChip } from '../components/tasks/TagPeople';
import { taggedNotOwner, taggedTitle } from '../components/tasks/tagged';
import { notifyTasksChanged, useTasksChanged } from '../components/tasks/useTasks';
import { useCompleteTask } from '../components/tasks/useCompleteTask';
import { latestReads } from '../live/useLive';
import { canEdit } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import {
  buildTaskRows,
  cleanFilters,
  DEFAULT_FILTERS,
  DUE_FILTERS,
  dueRange,
  filterRows,
  groupByOwner,
  localDay,
  ownerOptions,
  sortRows,
  STATUS_FILTERS,
  TYPE_FILTERS,
} from '../components/tasks/tasksTable';

const GREEN = '#1B5E20';
const RED = '#B71C1C';
const FONT = "'Montserrat', sans-serif";
const LIST_CAP = 2000; // the API's LIMIT - more than this and some tasks are not loaded

const LABEL = { fontSize: 9, fontWeight: 800, color: '#888', letterSpacing: 1.2, textTransform: 'uppercase' };
const SELECT = {
  padding: '6px 9px',
  borderRadius: 6,
  border: '1px solid #D8DED4',
  fontSize: 11,
  fontFamily: FONT,
  background: '#fff',
  color: '#0D0D0D',
  cursor: 'pointer',
  outline: 'none',
};
const tag = { display: 'inline-block', padding: '1px 7px', borderRadius: 8, fontSize: 9, fontWeight: 800, whiteSpace: 'nowrap' };
const th = {
  padding: '9px 12px',
  textAlign: 'left',
  fontSize: 9,
  fontWeight: 800,
  color: '#888',
  letterSpacing: 1,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #ECECEC',
  background: '#FAFBF9',
};
const td = { padding: '9px 12px', fontSize: 11, color: '#333', borderBottom: '1px solid #F4F4F4', verticalAlign: 'top' };
// the done checkbox sits in the Task cell, not a blank column of its own (10/9/26)
const COLUMNS = ['Task', 'Owner', 'Due', 'Type', 'Home record', 'Contact', 'Stage'];

/* Per-user, per-browser filters. Storage can be blocked or empty - the page
   then opens on the defaults and simply does not remember this time. */
const storeKey = (email) =>
  `globalops_tasks_filters:${String(email || '')
    .trim()
    .toLowerCase()}`;
function readFilters(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return cleanFilters(raw ? JSON.parse(raw) : null);
  } catch {
    return cleanFilters(null);
  }
}
function useStoredFilters(email) {
  const key = storeKey(email);
  const [state, setState] = useState(() => ({ key, filters: readFilters(key) }));
  // another person signed in on this browser: their own filters
  if (state.key !== key) setState({ key, filters: readFilters(key) });
  const update = useCallback((patch) => {
    setState((cur) => {
      const next = cleanFilters(patch === null ? null : { ...cur.filters, ...patch });
      try {
        window.localStorage.setItem(cur.key, JSON.stringify(next));
      } catch {
        /* not remembered this time */
      }
      return { key: cur.key, filters: next };
    });
  }, []);
  return [state.key === key ? state.filters : readFilters(key), update];
}

const sameFilters = (a, b) => Object.keys(DEFAULT_FILTERS).every((k) => a[k] === b[k]);

function Toggle({ options, value, onChange, label }) {
  return (
    <div
      role="group"
      aria-label={label}
      style={{ display: 'inline-flex', borderRadius: 8, border: '1px solid #D8DED4', overflow: 'hidden', flexShrink: 0 }}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          style={{
            padding: '6px 14px',
            border: 'none',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 700,
            fontFamily: FONT,
            background: value === o.value ? GREEN : '#fff',
            color: value === o.value ? '#fff' : '#555',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Picker({ id, label, value, onChange, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <label htmlFor={id} style={LABEL}>
        {label}
      </label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} style={SELECT}>
        {children}
      </select>
    </span>
  );
}

function TaskRow({ r, onToggle, busy, taggedMe }) {
  const t = r.task;
  const done = t.status !== 'open';
  const note = done && t.completion_note ? String(t.completion_note) : '';
  return (
    <tr>
      <td style={{ ...td, minWidth: 240, maxWidth: 400 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={done}
            disabled={busy}
            onChange={() => onToggle(r)}
            title={done ? 'Reopen' : 'Mark complete'}
            aria-label={`${done ? 'Reopen' : 'Mark complete'}: ${r.description}`}
            style={{ cursor: busy ? 'default' : 'pointer', margin: '2px 0 0', flexShrink: 0 }}
          />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 600,
                color: done ? '#8A8A8A' : '#0D0D0D',
                lineHeight: 1.45,
                wordBreak: 'break-word',
                textDecoration: done ? 'line-through' : 'none',
              }}
            >
              {r.description}
              {taggedMe && <TaggedChip style={{ marginLeft: 6, verticalAlign: 1 }} />}
            </div>
            {note && (
              <div style={{ marginTop: 3, fontSize: 10.5, color: '#666', fontStyle: 'italic', wordBreak: 'break-word' }}>Note: {note}</div>
            )}
          </div>
        </div>
      </td>
      <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700, color: done ? '#999' : GREEN }}>
        {r.owner || '-'}
        {r.tagged.length > 0 && (
          <div
            title={taggedTitle(r.tagged)}
            style={{ marginTop: 2, fontSize: 9.5, fontWeight: 600, color: done ? '#999' : '#1F4E8C', whiteSpace: 'normal', maxWidth: 180 }}
          >
            + {r.tagged.join(', ')}
          </div>
        )}
      </td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        {r.due ? (
          <span style={{ fontWeight: 800, color: r.overdue ? RED : '#1565C0' }}>
            {r.overdue ? 'OVERDUE · ' : ''}
            {fmtDate(r.due)}
          </span>
        ) : (
          <span style={{ color: '#bbb' }}>-</span>
        )}
        {/* the local day it was done: the ISO string's own date is the UTC one, a day ahead in the evening */}
        {done && r.completedDay && <div style={{ fontSize: 9.5, color: '#999', marginTop: 2 }}>done {fmtDate(r.completedDay)}</div>}
      </td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        {r.typeTag ? <span style={{ ...tag, color: r.typeTag.fg, background: r.typeTag.bg }}>{r.typeTag.text}</span> : ''}
      </td>
      <td style={{ ...td, maxWidth: 260 }}>
        {r.homeHash ? (
          <a
            href={r.homeHash}
            title={`Open ${r.homeLabel || 'the record'}`}
            style={{ color: '#1565C0', fontWeight: 700, textDecoration: 'none', wordBreak: 'break-word' }}
          >
            {r.homeLabel || 'Open record'}
          </a>
        ) : (
          <span style={{ color: '#777', wordBreak: 'break-word' }}>{r.homeLabel || '-'}</span>
        )}
      </td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        {r.contact ? <ContactLink contact={r.contact} /> : <span style={{ color: '#bbb' }}>-</span>}
      </td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        {r.stage ? (
          <span style={{ fontWeight: 700, color: STAGE_COLORS[r.stage] || '#555' }}>{r.stage}</span>
        ) : (
          <span style={{ color: '#bbb' }}>-</span>
        )}
      </td>
    </tr>
  );
}

export default function TasksView() {
  const { allAccounts, allProjects, users } = useApp();
  // Tasks View: the list reads, the tick boxes do not (the server refuses too)
  const readOnly = !canEdit(useMyAccess(), 'tasks');
  const { user } = useAuth();
  const [filters, setFilters] = useStoredFilters(user && user.email);
  const { complete, prompt } = useCompleteTask();

  // the list for the chosen status: open, or completed (fetched only when asked for)
  const [items, setItems] = useState(null); // null = loading
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [reopening, setReopening] = useState(null);
  // Its load, the re-read after a change here and a live one (someone else's
  // change) can overlap: the newest answer GOT wins (latestReads). The old
  // rule - the newest STARTED read wins - let a failed live re-read throw away
  // the re-read this person was waiting for.
  const [reads] = useState(latestReads);
  const status = filters.status;
  const load = useCallback(
    ({ live } = {}) => {
      const ticket = reads.start();
      // A read that fails keeps the list on screen. Only one the person is
      // waiting for says so - someone else's change (live) re-reads quietly -
      // and only while nothing newer has been shown.
      const fail = (message) => {
        if (live || !reads.show(ticket)) return;
        setLoadError(message);
        setItems((cur) => cur || []);
      };
      fetch(`/api/crm-actions?status=${encodeURIComponent(status)}`)
        .then(async (r) => {
          if (r.status === 403) {
            if (reads.show(ticket)) {
              setItems([]);
              setLoadError('Your role does not have access to tasks.');
            }
            return;
          }
          const body = await r.json().catch(() => null);
          if (!r.ok || !Array.isArray(body)) {
            fail((body && body.error) || 'Could not load the tasks - try again.');
            return;
          }
          if (reads.show(ticket)) {
            setLoadError('');
            setItems(body);
          }
        })
        .catch(() => fail('Could not load the tasks - check the connection and try again.'));
    },
    [status, reads]
  );
  // Open <-> Completed: a read of the other list still on its way never lands in this one
  useEffect(() => {
    reads.show(reads.start());
    setItems(null);
    load();
  }, [load, reads]);
  // a task completed or added anywhere else shows here too
  useTasksChanged(load);

  const today = localDay();
  const accountsById = useMemo(() => indexById(allAccounts), [allAccounts]);
  const projectsById = useMemo(() => indexById(allProjects), [allProjects]);
  const contactsById = useMemo(() => contactIndex(allAccounts), [allAccounts]);
  const rows = useMemo(
    () => buildTaskRows(items || [], { accountsById, projectsById, contactsById, users, today }),
    [items, accountsById, projectsById, contactsById, users, today]
  );
  const shown = useMemo(
    () => sortRows(filterRows(rows, filters, { today, user, users }), filters.status, today),
    [rows, filters, today, user, users]
  );
  const groups = useMemo(() => (filters.group ? groupByOwner(shown) : null), [filters.group, shown]);
  const people = useMemo(() => {
    const list = ownerOptions(items || [], users);
    // a remembered person with nothing open right now still shows as picked
    const picked = filters.owner;
    return picked !== 'all' && picked !== 'me' && !list.some((n) => n.toLowerCase() === picked.toLowerCase())
      ? [...list, picked].sort((a, b) => a.localeCompare(b))
      : list;
  }, [items, users, filters.owner]);
  const overdue = shown.filter((r) => r.overdue).length;
  const range = dueRange(filters.due, today, { from: filters.from, to: filters.to });

  const reopen = async (r) => {
    setReopening(r.id);
    setActionError('');
    try {
      const res = await fetch(`/api/crm-actions/${encodeURIComponent(r.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'open' }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        setActionError(b.error || `Could not reopen "${r.description}" - try again.`);
      }
    } catch {
      setActionError(`Could not reopen "${r.description}" - try again.`);
    } finally {
      setReopening(null);
      notifyTasksChanged();
    }
  };
  const toggle = (r) => {
    setActionError('');
    if (r.task.status === 'open') complete(r.task);
    else reopen(r);
  };

  const loadingList = items === null;
  const noun = filters.status === 'open' ? 'open' : 'completed';
  const rangeText =
    range && filters.due !== 'overdue'
      ? `${range.from ? fmtDate(range.from) : 'any date'} to ${range.to ? fmtDate(range.to) : 'any date'}`
      : '';

  return (
    <div style={{ fontFamily: FONT }}>
      <div
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}
      >
        <div>
          <div className="text-eyebrow uppercase text-text-muted mb-1">Workspace · Tasks</div>
          <p className="text-body-sm text-text-body m-0">
            {loadingList ? (
              'Loading tasks…'
            ) : (
              <>
                <b className="text-text-dark tabular-nums">{shown.length}</b> {noun} {shown.length === 1 ? 'task' : 'tasks'}
                {overdue > 0 && (
                  <>
                    {' '}
                    · <b style={{ color: RED }}>{overdue} overdue</b>
                  </>
                )}
                {!sameFilters(filters, DEFAULT_FILTERS) ? ' match the filters' : ''}
              </>
            )}
          </p>
        </div>
        <Toggle label="Open or completed" options={STATUS_FILTERS} value={filters.status} onChange={(v) => setFilters({ status: v })} />
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <Picker id="tasks-owner" label="Person" value={filters.owner} onChange={(v) => setFilters({ owner: v })}>
          <option value="all">Everyone</option>
          <option value="me">Me</option>
          {people.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Picker>
        <Picker id="tasks-type" label="Type" value={filters.type} onChange={(v) => setFilters({ type: v })}>
          {TYPE_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </Picker>
        <Picker id="tasks-due" label="Due" value={filters.due} onChange={(v) => setFilters({ due: v })}>
          {DUE_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </Picker>
        {filters.due === 'custom' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input
              type="date"
              aria-label="Due from"
              value={filters.from}
              onChange={(e) => setFilters({ from: e.target.value })}
              style={SELECT}
            />
            <span style={{ fontSize: 10.5, color: '#888' }}>to</span>
            <input type="date" aria-label="Due to" value={filters.to} onChange={(e) => setFilters({ to: e.target.value })} style={SELECT} />
          </span>
        )}
        {rangeText && <span style={{ fontSize: 10.5, color: '#777' }}>{rangeText}</span>}
        <button
          type="button"
          onClick={() => setFilters({ group: !filters.group })}
          aria-pressed={filters.group}
          style={{
            padding: '5px 12px',
            borderRadius: 16,
            fontSize: 10.5,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: FONT,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: filters.group ? GREEN : '#E4E4E4',
            background: filters.group ? GREEN : '#fff',
            color: filters.group ? '#fff' : '#666',
          }}
        >
          Group by person
        </button>
        {!sameFilters(filters, DEFAULT_FILTERS) && (
          <button
            type="button"
            onClick={() => setFilters(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 10.5,
              fontWeight: 700,
              color: GREEN,
              fontFamily: FONT,
              padding: 0,
            }}
          >
            Reset filters
          </button>
        )}
      </div>

      {(loadError || actionError) && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: '#FFEBEE',
            border: '1px solid #FFCDD2',
            color: RED,
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 11.5,
            fontWeight: 600,
            marginBottom: 12,
          }}
        >
          <span style={{ flex: 1 }}>{loadError || actionError}</span>
          {loadError && (
            <button
              type="button"
              onClick={() => load()}
              style={{
                padding: '4px 12px',
                borderRadius: 6,
                border: `1px solid ${RED}`,
                background: '#fff',
                color: RED,
                fontSize: 10.5,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: FONT,
              }}
            >
              Try again
            </button>
          )}
        </div>
      )}
      {!loadingList && (items || []).length >= LIST_CAP && (
        <div
          style={{
            background: '#FFF8E1',
            border: '1px solid #FFE082',
            color: '#8D6E00',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 11,
            marginBottom: 12,
          }}
        >
          Only the first {LIST_CAP.toLocaleString('en-US')} {noun} tasks were loaded, so some may be missing from this list.
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ECECEC', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c} style={th}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups
                ? groups.map((g) => [
                    <tr key={`g:${g.key}`}>
                      <td
                        colSpan={COLUMNS.length}
                        style={{
                          padding: '8px 12px',
                          background: '#F6F9F3',
                          borderBottom: '1px solid #E5E9DD',
                          fontSize: 11,
                          fontWeight: 800,
                          color: '#0D0D0D',
                        }}
                      >
                        {g.owner}
                        <span style={{ fontWeight: 600, color: '#777', marginLeft: 8 }}>
                          {g.rows.length} {g.rows.length === 1 ? 'task' : 'tasks'}
                          {g.overdue > 0 && <span style={{ color: RED, fontWeight: 800 }}> · {g.overdue} overdue</span>}
                        </span>
                      </td>
                    </tr>,
                    ...g.rows.map((r) => (
                      <TaskRow
                        key={r.id}
                        r={r}
                        onToggle={toggle}
                        busy={reopening === r.id || readOnly}
                        taggedMe={taggedNotOwner(r.task, user, users)}
                      />
                    )),
                  ])
                : shown.map((r) => (
                    <TaskRow
                      key={r.id}
                      r={r}
                      onToggle={toggle}
                      busy={reopening === r.id || readOnly}
                      taggedMe={taggedNotOwner(r.task, user, users)}
                    />
                  ))}
            </tbody>
          </table>
          {loadingList && <div style={{ textAlign: 'center', padding: 40, color: '#999', fontSize: 11 }}>Loading tasks…</div>}
          {!loadingList && !loadError && shown.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#aaa', fontSize: 11 }}>
              {(items || []).length === 0 ? `No ${noun} tasks.` : 'No tasks match these filters.'}
            </div>
          )}
        </div>
      </div>
      {prompt}
    </div>
  );
}
